import { Component, DestroyRef, HostListener, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, Subject, debounceTime } from 'rxjs';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Params, Router, RouterLink, RouterOutlet } from '@angular/router';
import { typesForScope } from '../../../core/occurrence/occurrence-catalog';
import {
  NATURE_ICONS, NATURE_LABELS,
  ORIGIN_LABELS, ORIGIN_OPTIONS, SCOPE_LABELS, SCOPE_OPTIONS, SITUATION_OPTIONS, STATE_OPTIONS, TYPE_ICONS, TYPE_LABELS, isSensitiveType, stateBadgeClass, stateBadgeLabel,
} from '../../../core/occurrence/occurrence.labels';
import {
  LinkedOccurrence, Occurrence, OccurrenceScope, OccurrenceType,
} from '../../../core/occurrence/occurrence.models';
import { AuthService } from '../../../core/auth/auth.service';
import { OccurrenceService } from '../../../core/occurrence/occurrence.service';
import { ToastService } from '../../../core/ui/toast.service';
import { ConfirmDialog } from '../../../shared/confirm-dialog/confirm-dialog';
import { PageHeader } from '../../../shared/page-header/page-header';
import { Pagination } from '../../../shared/pagination/pagination';

@Component({
  selector: 'app-occurrence-list',
  imports: [RouterLink, RouterOutlet, FormsModule, DatePipe, PageHeader, Pagination, ConfirmDialog],
  templateUrl: './occurrence-list.html',
})
export class OccurrenceList implements OnInit {
  private readonly service = inject(OccurrenceService);
  private readonly toasts = inject(ToastService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  /** O pedido em curso: um filtro novo cancela o anterior, para uma resposta lenta não o atropelar. */
  private fetching?: Subscription;
  /** A escrita no campo de busca espera uma pausa, em vez de pedir à API a cada tecla. */
  private readonly search$ = new Subject<void>();
  protected readonly auth = inject(AuthService);

  protected readonly occurrences = signal<Occurrence[]>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);

  /**
   * Ocorrência à espera de confirmação para ser eliminada — a "Eliminação Total" que a lista
   * passou a oferecer (documento de 2026-10-08). Não se desfaz, por isso pergunta-se primeiro.
   */
  protected readonly pendingRemoval = signal<Occurrence | null>(null);

  /**
   * Em que ponto vai a eliminação: primeiro confirma-se, e, havendo ocorrências criadas a partir
   * desta, decide-se o que lhes acontece. "Quando elimino a OC mãe a partir da lista já não me
   * faz nenhuma pergunta" (revisão de 2026-10-15) — e só depois de tudo respondido se elimina.
   */
  protected readonly removalStep = signal<'confirmar' | 'filhas'>('confirmar');

  /** As ocorrências criadas a partir da que se elimina — a segunda pergunta lista-as. */
  protected readonly pendingChildren = signal<readonly LinkedOccurrence[]>([]);

  protected askRemove(item: Occurrence, event: Event): void {
    event.stopPropagation();
    this.removalStep.set('confirmar');
    this.pendingRemoval.set(item);
  }

  protected cancelRemove(): void {
    this.pendingRemoval.set(null);
    this.removalStep.set('confirmar');
  }

  /** Esc no segundo passo (o do modal das filhas): volta à primeira pergunta, como o botão Voltar. */
  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.pendingRemoval() && this.removalStep() === 'filhas') this.backToConfirm();
  }

  /** O "sim" do primeiro passo: com filhas, falta decidir o que lhes acontece. */
  protected confirmRemove(): void {
    const item = this.pendingRemoval();
    if (!item) return;
    if (item.hasChildren && this.removalStep() === 'confirmar') {
      this.removalStep.set('filhas');
      // A pergunta nomeia as filhas, código e título (revisão de 2026-10-19).
      this.pendingChildren.set([]);
      this.service.links(item.id).subscribe({
        next: (links) => this.pendingChildren.set(links.children),
        error: () => this.pendingChildren.set([]),
      });
      return;
    }
    this.remove(item, 'MANTER');
  }

  /** Voltar à primeira pergunta sem eliminar nada. */
  protected backToConfirm(): void {
    this.removalStep.set('confirmar');
  }

  protected removeWithChildren(): void {
    const item = this.pendingRemoval();
    if (item) this.remove(item, 'ELIMINAR');
  }

  protected removeKeepingChildren(): void {
    const item = this.pendingRemoval();
    if (item) this.remove(item, 'MANTER');
  }

  private remove(item: Occurrence, children: 'ELIMINAR' | 'MANTER'): void {
    this.pendingRemoval.set(null);
    this.removalStep.set('confirmar');
    this.service.remove(item.id, children).subscribe({
      next: () => {
        this.toasts.success(item.hasChildren && children === 'ELIMINAR'
          ? `${item.code} eliminada, com as ocorrências criadas a partir dela.`
          : `${item.code} eliminada.`);
        this.load();
      },
      error: () => this.toasts.error('Não foi possível eliminar a ocorrência.'),
    });
  }

  /** O clique nos botões da linha não pode abrir a ocorrência por baixo. */
  protected stop(event: Event): void {
    event.stopPropagation();
  }

  protected readonly typeLabels = TYPE_LABELS;
  protected readonly typeIcons = TYPE_ICONS;
  /** A natureza é o que distingue uma reclamação de uma denúncia — na lista, fala mais que o tipo. */
  protected readonly natureLabels = NATURE_LABELS;
  protected readonly natureIcons = NATURE_ICONS;
  protected readonly originLabels = ORIGIN_LABELS;
  protected readonly scopeLabels = SCOPE_LABELS;
  protected readonly originOptions = ORIGIN_OPTIONS;
  protected readonly scopeOptions = SCOPE_OPTIONS;
  protected readonly stateOptions = STATE_OPTIONS;
  protected readonly situationOptions = SITUATION_OPTIONS;

  /**
   * Tipos do âmbito escolhido — sem âmbito, a união de todos, cada um uma só vez. Agrupar por
   * âmbito repetia as mesmas opções (36 para 15 tipos) sem as distinguir: o filtro envia só o
   * tipo, e a "Não conformidade" de um grupo filtrava exatamente o mesmo que a de outro.
   */
  protected typeOptions(): { value: OccurrenceType; label: string }[] {
    return typesForScope(this.scope as OccurrenceScope | null)
      .map((type) => ({ value: type, label: TYPE_LABELS[type] }));
  }

  /** Mudar o âmbito pode invalidar o tipo escolhido. */
  protected onScopeChange(): void {
    if (this.type && !this.typeOptions().some((option) => option.value === this.type)) {
      this.type = null;
    }
    this.load();
  }

  protected readonly currentYear = new Date().getFullYear();
  protected readonly years = [this.currentYear + 1, this.currentYear, this.currentYear - 1, this.currentYear - 2];

  protected q = '';
  /**
   * Abre em "Todos": com o ano corrente por omissão, uma ocorrência datada de outro ano
   * desaparecia da lista sem nada o indicar.
   */
  protected year: number | null = null;
  protected situation: string | null = null;
  protected state: string | null = null;
  protected scope: string | null = null;
  protected type: string | null = null;
  protected origin: string | null = null;
  /** 'aplicavel' | 'nao-aplicavel' | null (= todas); vai ao endereço como ?eficacia=. */
  protected efficacy: string | null = null;

  protected readonly pageSize = 10;
  protected readonly page = signal(1);
  protected readonly total = signal(0);
  /** Em mobile os filtros começam recolhidos (em ≥md o CSS mostra-os sempre). */
  protected readonly filtersOpen = signal(false);

  protected toggleFilters(): void {
    this.filtersOpen.update((open) => !open);
  }

  ngOnInit(): void {
    this.readUrl();
    this.search$.pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.load());
    // Fechar o painel de nova ocorrência larga o endereço dos filtros: repõe-no, sem nova entrada
    // no histórico, para que o refresh continue a abrir a mesma lista.
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationEnd && !this.route.firstChild && this.urlParamsDiffer()) {
        this.writeUrl();
      }
    });
    this.fetch();
  }

  protected onSearch(): void {
    this.search$.next();
  }

  /** Os filtros e a página vivem no endereço: sobrevivem ao refresh, ao voltar e ao partilhar o link. */
  private readUrl(): void {
    const q = this.route.snapshot.queryParamMap;
    const year = Number(q.get('ano'));
    this.q = q.get('q') ?? '';
    this.year = Number.isInteger(year) && year > 0 ? year : null;
    this.situation = q.get('situacao');
    this.state = q.get('estado');
    this.scope = q.get('ambito');
    this.type = q.get('tipo');
    this.origin = q.get('canal');
    const efficacy = q.get('eficacia');
    this.efficacy = efficacy === 'aplicavel' || efficacy === 'nao-aplicavel' ? efficacy : null;
    const page = Number(q.get('pagina'));
    this.page.set(Number.isInteger(page) && page > 0 ? page : 1);
  }

  private urlParams(): Params {
    return {
      q: this.q || null,
      ano: this.year,
      situacao: this.situation,
      estado: this.state,
      ambito: this.scope,
      tipo: this.type,
      canal: this.origin,
      eficacia: this.efficacy,
      pagina: this.page() > 1 ? this.page() : null,
    };
  }

  private urlParamsDiffer(): boolean {
    const current = this.route.snapshot.queryParamMap;
    return Object.entries(this.urlParams())
      .some(([key, value]) => (current.get(key) ?? null) !== (value === null ? null : String(value)));
  }

  private writeUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route, queryParams: this.urlParams(), queryParamsHandling: 'merge', replaceUrl: true,
    });
  }

  protected load(): void {
    this.page.set(1);
    this.fetch();
  }

  protected onPage(p: number): void {
    this.page.set(p);
    this.fetch();
  }

  private fetch(): void {
    this.loading.set(true);
    this.error.set(null);
    if (!this.route.firstChild && this.urlParamsDiffer()) this.writeUrl();
    this.fetching?.unsubscribe();
    this.fetching = this.service
      .list({
        q: this.q,
        year: this.year,
        situation: this.situation,
        state: this.state,
        scope: this.scope,
        type: this.type,
        origin: this.origin,
        effectivenessApplicable: this.efficacy === null ? null : this.efficacy === 'aplicavel',
        page: this.page() - 1,
        size: this.pageSize,
      })
      .subscribe({
        next: (res) => {
          // Um endereço com uma página que já não existe (apagaram-se ocorrências): volta à primeira.
          if (res.content.length === 0 && this.page() > 1 && res.totalElements > 0) {
            this.page.set(1);
            this.fetch();
            return;
          }
          this.occurrences.set(res.content);
          this.total.set(res.totalElements);
          this.loading.set(false);
        },
        error: () => {
          this.error.set('Não foi possível carregar as ocorrências.');
          this.loading.set(false);
        },
      });
  }

  /** Há algum filtro ativo? (o ano abre no corrente, por isso conta como filtro) */
  protected filtered(): boolean {
    return !!this.q || this.year !== null || !!this.situation || !!this.state || !!this.scope
      || !!this.type || !!this.origin || !!this.efficacy;
  }

  protected clearFilters(): void {
    this.q = '';
    this.year = null;
    this.situation = null;
    this.state = null;
    this.scope = null;
    this.type = null;
    this.origin = null;
    this.efficacy = null;
    this.load();
  }

  /** Verificação da eficácia aplicável: null conta como aplicável, como no formulário. */
  protected efficacyApplicable(item: Occurrence): boolean {
    return item.effectivenessEvaluation !== 'NAO_APLICAVEL';
  }

  /** Denúncias e afins: sinal visual de circuito reservado. */
  protected sensitive(item: Occurrence): boolean {
    return isSensitiveType(item.nature);
  }

  /** Fechada tinge pelo tipo de fecho (verde/vermelho); estados a decorrer têm cor própria. */
  protected badgeClass(item: Occurrence): string {
    return stateBadgeClass(item);
  }

  /** Rótulo do badge: "Fechado · Com sucesso" quando fechada. */
  protected stateBadgeLabel(item: Occurrence): string {
    return stateBadgeLabel(item);
  }

  /** Cor da faixa lateral (sinal estrutural do registro de qualidade). */
  protected stateColor(item: Occurrence): string {
    if (item.situation === 'FECHADO') {
      return item.closingType === 'SEM_SUCESSO' ? 'var(--bs-danger)' : 'var(--bs-success)';
    }
    if (item.state === 'AVALIACAO_REAVALIACAO') return 'var(--bs-warning)';
    if (item.state === 'EM_TRATAMENTO') return 'var(--app-primary)';
    return 'var(--bs-info)';
  }
}
